import { useApolloClient } from "@apollo/client";
import { useCallback, useState } from "react";

import { useTransferKanbanBoardOwnershipMutation } from "@/generated/graphql";

/**
 * Every operation in this feature that selects a board role.
 *
 * The mutation returns the INCOMING owner's row alone; the incumbent's demotion
 * to EDITOR happens in the same transaction but is absent from the payload, so
 * a normalized cache write updates one of the two rows that changed. Anything
 * rendering a role has to be refetched or it keeps showing the previous owner.
 */
const ROLE_BEARING_QUERIES = [
  "GetKanbanBoardWithPermissions",
  "ListMyKanbanBoards",
  "PipelineSwitcherBoards",
];

/**
 * Result of the board ownership transfer hook.
 */
export interface UseBoardOwnershipTransferResult {
  /**
   * Moves the board's single OWNER row to `newOwnerUserId`.
   *
   * Rejects when the MUTATION fails, carrying the server's message. Resolves
   * once the transfer has committed, even if the follow-up refetch fails.
   */
  transferOwnership: (newOwnerUserId: string) => Promise<void>;
  /** Whether a transfer, including its refetch window, is in flight. */
  isTransferring: boolean;
}

/**
 * SE-8296 — the only client path that moves kanban board ownership.
 *
 * ## Why the refetch is settled separately rather than awaited by Apollo
 *
 * `awaitRefetchQueries: true` makes Apollo resolve the mutation from a
 * `Promise.all` over the refetch promises. A network failure on ANY refetch
 * then rejects the mutation promise **after the server has already committed**,
 * and the caller reports "Failed to transfer ownership" for a transfer that
 * happened. The user retries, is now an EDITOR because the demotion succeeded,
 * and the server answers Forbidden — so the second message names the wrong
 * cause and nothing ever tells them the transfer went through. A wrong error
 * about a successful action is worse than a plain one.
 *
 * So the mutation is awaited alone, and the refetches are settled here where a
 * failure can be swallowed. The same trade every sibling in this feature makes:
 * `useBoardSharing` passes `refetchQueries` with no await, and
 * `useKanbanBoardMutations` sets `awaitRefetchQueries: false` explicitly.
 *
 * ## The property this deliberately keeps
 *
 * `isTransferring` stays true across the mutation AND the refetch, and the
 * returned promise resolves only once both have settled — so a caller that
 * closes a dialog on resolution never flashes the pre-transfer roles. That is
 * why this is local state rather than Apollo's `loading`, which would drop to
 * false as soon as the mutation alone resolved.
 * @param boardId - The board whose ownership is moving
 * @returns The transfer callback and its in-flight flag
 */
export const useBoardOwnershipTransfer = (
  boardId: string | undefined
): UseBoardOwnershipTransferResult => {
  const client = useApolloClient();
  const [transferMutation] = useTransferKanbanBoardOwnershipMutation();
  const [isTransferring, setIsTransferring] = useState(false);

  const transferOwnership = useCallback(
    async (newOwnerUserId: string) => {
      if (!boardId) {
        throw new Error("Board ID is required");
      }

      setIsTransferring(true);
      try {
        await transferMutation({
          variables: {
            input: {
              kanbanBoardId: boardId,
              newOwnerUserId,
            },
          },
        });

        // Past this line the transfer is COMMITTED. A refetch failure leaves
        // roles stale until the next natural fetch; it never means the transfer
        // failed, so it must not reach the caller.
        await client
          .refetchQueries({ include: ROLE_BEARING_QUERIES })
          .catch(() => undefined);
      } finally {
        setIsTransferring(false);
      }
    },
    [boardId, transferMutation, client]
  );

  return {
    transferOwnership,
    isTransferring,
  };
};
